import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Eye, Heart, MessageCircle, Share, Facebook, Linkedin, Instagram, Settings2 } from 'lucide-react';
import { Button } from '../ui/button';
import SocialSetupGuide from './SocialSetupGuide';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_CONFIG = {
  facebook: { label: 'Facebook', color: '#1877f2', Icon: Facebook },
  instagram: { label: 'Instagram', color: '#e4405f', Icon: Instagram },
  linkedin: { label: 'LinkedIn', color: '#0a66c2', Icon: Linkedin },
  google_business: { label: 'Google', color: '#4285f4', Icon: null },
  gbp: { label: 'Google', color: '#4285f4', Icon: null },
};

export default function ClientSocial({ client, getHeaders }) {
  const { t } = useTranslation('dashboard');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('posts');

  useEffect(() => {
    fetch(`${API}/api/client/dashboard/social`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getHeaders]);

  if (loading) return <div className="text-gray-400 animate-pulse py-12 text-center">{t('social.loading')}</div>;

  const posts = data?.posts || [];
  const totalEngagement = posts.reduce((sum, p) => sum + (p.engagement?.views || 0) + (p.engagement?.likes || 0), 0);

  return (
    <div className="space-y-6" data-testid="social-tab">
      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('social.title')}</h2>
          <p className="text-xs text-gray-400">{t('social.subtitle')}</p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setTab('posts')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'posts' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="social-tab-posts">{t('social.tabPosts')}</button>
          <button onClick={() => setTab('setup')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === 'setup' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="social-tab-setup">
            <Settings2 className="w-3 h-3 inline mr-1" />{t('social.tabSetup')}
          </button>
        </div>
      </div>

      {/* Setup Guides Tab */}
      {tab === 'setup' && (
        <SocialSetupGuide client={client} getHeaders={getHeaders} />
      )}

      {/* Posts Tab */}
      {tab === 'posts' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="bg-white border-gray-200">
              <CardContent className="pt-5 text-center">
                <p className="text-xs text-gray-500 uppercase">{t('social.totalPosts')}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{data?.total || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="pt-5 text-center">
                <p className="text-xs text-gray-500 uppercase">{t('social.platforms')}</p>
                <div className="flex justify-center gap-2 mt-2 flex-wrap">
                  {data?.platforms && Object.entries(data.platforms).map(([p, count]) => {
                    const cfg = PLATFORM_CONFIG[p] || {};
                    return (
                      <Badge key={p} className="text-xs" style={{backgroundColor: `${cfg.color || '#6b7280'}20`, color: cfg.color || '#9ca3af'}}>
                        {cfg.label || p}: {count}
                      </Badge>
                    );
                  })}
                  {(!data?.platforms || Object.keys(data.platforms).length === 0) && (
                    <span className="text-xs text-gray-400">{t('social.noPlatforms')}</span>
                  )}
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200">
              <CardContent className="pt-5 text-center">
                <p className="text-xs text-gray-500 uppercase">{t('social.totalEngagement')}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{totalEngagement.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {/* No posts — show setup prompt */}
          {posts.length === 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-8 text-center">
                <Settings2 className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <h3 className="text-sm font-semibold text-blue-900 mb-1">{t('social.getStartedTitle')}</h3>
                <p className="text-xs text-blue-700 mb-4 max-w-md mx-auto">{t('social.getStartedDesc')}</p>
                <Button size="sm" onClick={() => setTab('setup')} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="go-to-setup">
                  <Settings2 className="w-3 h-3 mr-1" /> {t('social.viewSetupGuides')}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Recent Posts */}
          {posts.length > 0 && (
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-gray-900 text-base">{t('social.recentPosts')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {posts.map((post, i) => {
                  const cfg = PLATFORM_CONFIG[post.platform] || {};
                  const eng = post.engagement || {};
                  return (
                    <div key={i} className="flex gap-4 pb-4 border-b border-gray-200 last:border-0 last:pb-0">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{backgroundColor: `${cfg.color || '#6b7280'}20`}}>
                        {cfg.Icon ? <cfg.Icon className="h-5 w-5" style={{color: cfg.color}} /> : <Share className="h-5 w-5 text-gray-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="text-[10px]" style={{backgroundColor: `${cfg.color || '#6b7280'}20`, color: cfg.color || '#9ca3af'}}>
                            {cfg.label || post.platform}
                          </Badge>
                          <span className="text-[10px] text-gray-500">
                            {post.posted_at ? new Date(post.posted_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{post.content}</p>
                        <div className="flex gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {eng.views || 0}</span>
                          <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {eng.likes || 0}</span>
                          <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" /> {eng.comments || 0}</span>
                          <span className="flex items-center gap-1"><Share className="h-3 w-3" /> {eng.shares || 0}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
